import { createStore } from '@/src/hooks/store/createStore';

export interface ChatMessage {
  messageId: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  feedbackRating?: number | null;
  asyncInProgress: boolean;
}

export interface TeammateStore {
  setSessionId: (s: string) => void;
  sessionId: string;
  // resetConversation: () => void;
  chatHistory: ChatMessage[];
  setChatHistory: (history: ChatMessage[]) => void;
  // restoredMessageCount: number;
  appendMessage: (message: ChatMessage) => void;
  // initialize: (eventId?: string | null) => Promise<void>;
  updateLastMessage: (overrides: Partial<ChatMessage>) => void;
  loading: boolean;
  setLoading: (l: boolean) => void;
  restoredMessages: ChatMessage[];
  setRestoredMessages: (msgs: ChatMessage[]) => void;
  // Tri-state: undefined = not yet determined (initializing), true = active, false =
  // confirmed disabled. Starts undefined so consumers can distinguish the initial
  // "unknown" render from a confirmed-disabled state and avoid flashing the
  // "unavailable" message before JamEventView's effect resolves the real value.
  aiTeammateActive: boolean | undefined;
  setAiTeammateActive: (enable?: boolean) => void;
  viewModalityTransfer: boolean;
  setViewModalityTransfer: (enabled: boolean) => void;
  feedbackBubbleShown: boolean;
  shouldShowFeedbackBubble: () => boolean;
  setFeedbackBubbleShow: (shown: boolean) => void;
}

/**
 * Cache of stores keyed by eventId so that all consumers of a given event share a
 * single store instance. Without this, every call to useAITeammate(eventId) would
 * build a brand-new store, so state updates in one consumer (e.g. JamEventView)
 * would not be reflected in another (e.g. Header).
 *
 * IMPORTANT: all consumers must pass the SAME identifier for a given event (the
 * resolved eventName), or they will resolve to different store instances and their
 * state will not be shared.
 */
const storeCache = new Map<string, ReturnType<typeof createStore<TeammateStore>>>();

/**
 * Builds an in-memory-only AI Teammate store.
 *
 * Persistence is intentionally DISABLED: `createStore` is called with a null name so
 * the zustand `persist` middleware is skipped entirely. Chat history and session id
 * are sensitive conversation data, so they must not be written to localStorage or
 * survive across sessions (privacy/security). All state — including transient fields
 * like `loading` and `aiTeammateActive` — lives only for the current session.
 */
const buildStore = () =>
  createStore<TeammateStore>(null, (set, get) => ({
    setSessionId: (sessionId: string) => set({ sessionId }),
    sessionId: '',
    chatHistory: [],
    setChatHistory: (chatHistory: ChatMessage[]) => set({ chatHistory }),
    appendMessage: (message: ChatMessage) => set((state) => ({ chatHistory: [...state.chatHistory, message] })),
    updateLastMessage: (overrides: Partial<ChatMessage>) =>
      set((state) => {
        // Guard on length: an empty array would make next[next.length - 1] resolve
        // to index -1, silently corrupting the array instead of updating a message.
        if (!state.chatHistory?.length) return {};
        const next = [...state.chatHistory];
        next[next.length - 1] = { ...next[next.length - 1], ...overrides };
        return { chatHistory: next };
      }),
    loading: false,
    setLoading: (loading: boolean) => set({ loading }),
    restoredMessages: [],
    setRestoredMessages: (restoredMessages: ChatMessage[]) => set({ restoredMessages }),
    aiTeammateActive: undefined,
    setAiTeammateActive: (aiTeammateActive?: boolean) => set({ aiTeammateActive }),
    viewModalityTransfer: false,
    setViewModalityTransfer: (viewModalityTransfer: boolean) => set({ viewModalityTransfer }),
    feedbackBubbleShown: false,
    shouldShowFeedbackBubble: () => {
      return get().chatHistory.filter((c) => c.role === 'user').length > 5 && !get().feedbackBubbleShown;
    },
    setFeedbackBubbleShow: (feedbackBubbleShown: boolean) => set({ feedbackBubbleShown }),
  }));

/**
 * A single store used before an event id is known. It is created lazily and reused so
 * that consumers rendering during this window still share state.
 */
let transientStore: ReturnType<typeof createStore<TeammateStore>> | null = null;

export const useAITeammate = (eventId: string) => {
  if (!eventId) {
    if (!transientStore) {
      transientStore = buildStore();
    }
    return transientStore;
  }

  let store = storeCache.get(eventId);
  if (!store) {
    store = buildStore();
    storeCache.set(eventId, store);
  }
  return store;
};

/**
 * Test-only helper. Clears the module-level store cache and transient store singleton so
 * each test starts from a clean slate. Without this, cached store instances (and their
 * in-memory state) leak across tests, making them order-dependent.
 */
export const resetStoresForTesting = () => {
  storeCache.clear();
  transientStore = null;
};
